import type { ReactElement, ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { AuthPageLoading } from "@/components/layout/AuthPageShell";
import { AuthLoadError } from "@/components/shared/AuthLoadError";
import { useAuth } from "@/hooks/useAuth";
import {
  acceptInviteLoginPath,
  needsOrgAcceptInvite,
  resolvePostAuthRoute,
} from "@/lib/authRouting";

type RequireAcceptInviteProps = {
  children: ReactNode;
};

export function RequireAcceptInvite({ children }: RequireAcceptInviteProps): ReactElement {
  const { user, isLoading, isError, refetch } = useAuth();
  const [searchParams] = useSearchParams();
  const organizationIdFromQuery = searchParams.get("organizationId")?.trim() || null;

  if (isLoading) return <AuthPageLoading />;

  if (isError) return <AuthLoadError onRetry={refetch} />;

  if (!user) return <Navigate to={acceptInviteLoginPath(organizationIdFromQuery)} replace />;
  if (user.needsPlatformInviteAccept) return <>{children}</>;

  const invitedViaQuery =
    !!organizationIdFromQuery &&
    user.organizations.some(
      (org) => org.id === organizationIdFromQuery && org.memberStatus === "invited",
    );
  if (invitedViaQuery) return <>{children}</>;

  if (user.needsWorkerOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!user.organization) return <Navigate to={resolvePostAuthRoute(user)} replace />;
  if (needsOrgAcceptInvite(user)) return <>{children}</>;
  if (user.memberStatus === "onboarded") {
    return <Navigate to={resolvePostAuthRoute(user)} replace />;
  }
  if (user.memberStatus !== "invited") {
    return <Navigate to={resolvePostAuthRoute(user)} replace />;
  }

  return <>{children}</>;
}
